import { getResponseBodyText, isAccessBlockedError, isKnownSearchAuthResponseBody } from './searchAccess.util';

export function createSearchAuth({ firebase, axios, mobileAppHeader = false }) {
  function clearSearchAuthHeader() {
    delete axios.defaults.headers.common.authorization;
    delete axios.defaults.headers.common.Authorization;
  }

  function ensureMobileAppHeader() {
    if (mobileAppHeader) {
      axios.defaults.headers.common['x-cardladder-mobile-app'] = 'true';
    }
  }

  function isFirebaseAuthExpiredError(error) {
    const code = error?.code || '';

    return code === 'auth/user-token-expired'
      || code === 'auth/user-disabled'
      || code === 'auth/user-not-found';
  }

  function createSessionExpiredError() {
    const error = new Error('Session expired');
    error.code = 'auth/user-token-expired';

    return error;
  }

  async function handleExpiredAuthSession() {
    clearSearchAuthHeader();

    try {
      await firebase.auth().signOut();
    } catch (e) {
      // swallow
    }
  }

  async function setSearchAuthHeader(forceRefresh = false) {
    const user = firebase.auth().currentUser;

    if (!user) {
      clearSearchAuthHeader();
      ensureMobileAppHeader();
      return null;
    }

    try {
      const token = await user.getIdToken(forceRefresh);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      if (mobileAppHeader) {
        axios.defaults.headers.common['x-cardladder-mobile-app'] = 'true';
      }

      return token;
    } catch (e) {
      if (isFirebaseAuthExpiredError(e)) {
        await handleExpiredAuthSession();
        return null;
      }

      throw e;
    }
  }

  function getHttpStatus(error) {
    return error?.response?.status ?? error?.statusCode ?? null;
  }

  function isSearchAuthError(error) {
    if (isAccessBlockedError(error)) {
      return false;
    }

    const status = getHttpStatus(error);

    if (status === 401) {
      return true;
    }

    if (status === 403) {
      return isKnownSearchAuthResponseBody(getResponseBodyText(error));
    }

    return false;
  }

  function isSessionExpiredError(error) {
    return isFirebaseAuthExpiredError(error) || error?.code === 'auth/not-authenticated';
  }

  async function withSearchAuthRetry(fn) {
    ensureMobileAppHeader();

    const user = firebase.auth().currentUser;

    if (user) {
      const token = await setSearchAuthHeader();

      if (!token) {
        throw createSessionExpiredError();
      }
    } else {
      clearSearchAuthHeader();
    }

    try {
      return await fn();
    } catch (e) {
      if (isAccessBlockedError(e)) {
        throw e;
      }

      if (isSearchAuthError(e) && firebase.auth().currentUser) {
        const retryToken = await setSearchAuthHeader(true);

        if (!retryToken) {
          throw createSessionExpiredError();
        }

        return fn();
      }

      throw e;
    }
  }

  return {
    clearSearchAuthHeader,
    ensureMobileAppHeader,
    isFirebaseAuthExpiredError,
    createSessionExpiredError,
    handleExpiredAuthSession,
    setSearchAuthHeader,
    getHttpStatus,
    isSearchAuthError,
    isSessionExpiredError,
    withSearchAuthRetry,
  };
}
